import { z } from "zod";
import { hasDisallowedPersonNameChars, normalizePersonNameInput } from "@goppy/domain";
import { GROUP_DISPLAY_NAME_INVALID_CHARS_MESSAGE } from "./groups.js";

/**
 * Shared DTO — Dependent (người phụ thuộc: con/vợ/khách) (US-E14.F2-0171 · @UC9191). MỘT nguồn sự
 * thật client↔server. @gate rbac.
 *
 * BẤT BIẾN (invariants) P6.2:
 * - Dependent = điểm HƯỞNG chi phí, KHÔNG account: KHÔNG có `userId` / auth trong schema (KHÁC
 *   MembershipAccount co-login). Body tạo/sửa KHÔNG nhận userId/id/groupId (server quyết).
 * - `DEPENDENT_KINDS` = nguồn sự thật đơn (mirror CHECK DB `dependents_kind_check` migration
 *   20260711180000) — drift-guard: enum ↔ whitelist DB lệch → int-spec đỏ.
 * - `.strict()` — reject field lạ (chống over-post / IDOR set userId/groupId).
 * - `defaultProxyMemberId` = member ĐẠI DIỆN gánh chi phí (proxy payer). Server validate member
 *   active cùng group (S03) — schema chỉ đảm bảo dạng UUID.
 */

/**
 * Loại người phụ thuộc. KHỚP TUYỆT ĐỐI whitelist CHECK DB `dependents_kind_check`.
 *  - child:  con.
 *  - spouse: vợ/chồng.
 *  - guest:  khách (đi cùng, được đại diện gánh).
 *  - other:  khác.
 * KHÔNG có 'pet' (ngoài whitelist) — int-spec khẳng định reject.
 */
export const DEPENDENT_KINDS = ["child", "spouse", "guest", "other"] as const;

export const dependentKindSchema = z.enum(DEPENDENT_KINDS);
export type DependentKind = z.infer<typeof dependentKindSchema>;

/**
 * Tên hiển thị dependent: 1..100 ký tự sau trim (chống rỗng / quá dài).
 *
 * 🔴 NAME-SANITIZE-REMAINING-DOORS (QĐ PO 03/10, @UC9191 @UC0055): tên người GÕ TAY ⇒ cùng chuẩn hoá + chốt ký tự tên người với biệt danh/tên
 * suất (`@goppy/domain` `person-name.ts`): ký tự ngoài allowlist ⇒ 400 `{ field: "displayName", reason: "format" }`, 0 ghi; chuỗi KIỂM = chuỗi LƯU.
 */
const displayNameSchema = z.preprocess(
  normalizePersonNameInput,
  z
    .string()
    .trim()
    .min(1, "Tên người phụ thuộc không được để trống")
    .max(100, "Tên người phụ thuộc tối đa 100 ký tự")
    .refine((name) => !hasDisallowedPersonNameChars(name), GROUP_DISPLAY_NAME_INVALID_CHARS_MESSAGE),
);

/**
 * Body tạo dependent (POST /v1/groups/:id/dependents). `.strict()` chặn field lạ (userId/groupId/id
 * — server quyết, không tin body).
 */
export const createDependentSchema = z
  .object({
    displayName: displayNameSchema,
    kind: dependentKindSchema,
    defaultProxyMemberId: z.string().uuid(),
    payerUnitId: z.string().uuid().optional(),
  })
  .strict();

export type CreateDependentRequest = z.infer<typeof createDependentSchema>;

/**
 * Body sửa dependent (PATCH /v1/groups/:id/dependents/:did). Mọi field optional (partial update),
 * `.strict()` chặn field lạ. Đổi defaultProxyMemberId ⇒ server validate member active cùng group.
 */
export const updateDependentSchema = z
  .object({
    displayName: displayNameSchema.optional(),
    kind: dependentKindSchema.optional(),
    defaultProxyMemberId: z.string().uuid().optional(),
    payerUnitId: z.string().uuid().nullable().optional(),
  })
  .strict();

export type UpdateDependentRequest = z.infer<typeof updateDependentSchema>;

/** Response 1 dependent — KHÔNG lộ userId/auth (dependent không có account). */
export const dependentResponseSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string(),
  kind: dependentKindSchema,
  defaultProxyMemberId: z.string().uuid(),
  payerUnitId: z.string().uuid().nullable(),
});

export type DependentResponse = z.infer<typeof dependentResponseSchema>;

/** Response danh sách dependent (chỉ dependent chưa xoá mềm). */
export const dependentListResponseSchema = z.object({
  items: z.array(dependentResponseSchema),
});

export type DependentListResponse = z.infer<typeof dependentListResponseSchema>;
